// src/components/Qbus/QbusControlPanel.jsx
// Main control panel for a location's Qbus outputs
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  FiberManualRecord as DotIcon,
  CreateNewFolder as CreateFolderIcon,
  Folder as FolderIcon,
  FolderOff as FolderOffIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoFixHigh as BulkNameIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { qbusApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useQbusWebSocket } from '../../hooks/useQbusWebSocket';
import QbusOutputCard from './QbusOutputCard';
import BulkFriendlyNameDialog from './BulkFriendlyNameDialog';

// Tab value constants
const TAB_ALL = 'all';
const TAB_UNORGANIZED = 'unorganized';

const QbusControlPanel = ({ locationId, locationName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission, user } = usePermissions();
  const { t } = useThemeSettings();

  // Data state
  const [outputs, setOutputs] = useState([]);
  const [outputTypes, setOutputTypes] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [total, setTotal] = useState(0);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [selectedTab, setSelectedTab] = useState(TAB_ALL);
  const [showHidden, setShowHidden] = useState(false);

  // Folder management dialog
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [bulkNameDialogOpen, setBulkNameDialogOpen] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyOutput, setHistoryOutput] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check permissions
  const isAdmin = hasPermission('admin') || hasPermission('qbus.admin');
  const canControl = hasPermission('qbus.outputs.control') || isAdmin;
  const canEdit = hasPermission('qbus.outputs.edit') || isAdmin;

  // WebSocket for live updates
  const { isConnected, connectionStatus } = useQbusWebSocket({
    locationId,
    onOutputStateChanged: useCallback((data) => {
      if (!data?.output) return;
      const updated = data.output;
      setOutputs(prev => prev.map(o =>
        o.id === updated.id
          ? { ...o, current_value: updated.current_value, last_state_change: updated.last_state_change }
          : o
      ));
    }, []),
    onControllerStatusChanged: useCallback((data) => {
      if (!data?.controller) return;
      const updated = data.controller;
      setControllers(prev => prev.map(c =>
        c.id === updated.id
          ? { ...c, status: updated.status }
          : c
      ));
    }, []),
    onConfigUpdated: useCallback(() => {
      // Refetch outputs when config changes (new outputs may have been added)
      fetchOutputs(true);
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
  });

  // Fetch output types
  const fetchOutputTypes = useCallback(async () => {
    try {
      const response = await qbusApi.getOutputTypes();
      if (response.data.outputTypes) {
        setOutputTypes(response.data.outputTypes);
      }
    } catch (error) {
      console.error('Error fetching output types:', error);
    }
  }, []);

  // Fetch controllers for location
  const fetchControllers = useCallback(async () => {
    try {
      const response = await qbusApi.getControllersForLocation(locationId);
      if (response.data.controllers) {
        setControllers(response.data.controllers);
      }
    } catch (error) {
      console.error('Error fetching controllers:', error);
    }
  }, [locationId]);

  // Fetch folders for location
  const fetchFolders = useCallback(async () => {
    try {
      const response = await qbusApi.getFolders(locationId);
      if (response.data.folders) {
        setFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [locationId]);

  // Fetch outputs for location
  const fetchOutputs = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (search) params.search = search;
      if (showOnlyAssigned) params.assignedOnly = true;

      // Folder filter
      if (selectedTab === TAB_UNORGANIZED) {
        params.folderId = 'none';
      } else if (selectedTab !== TAB_ALL) {
        params.folderId = selectedTab;
      }

      const response = await qbusApi.getOutputsForLocation(locationId, params);

      if (response.data) {
        let filteredOutputs = response.data.outputs || [];

        // Client-side type filter
        if (selectedType) {
          filteredOutputs = filteredOutputs.filter(o => o.output_type_id === selectedType);
        }

        // Filter locked outputs for non-admins
        if (!isAdmin) {
          filteredOutputs = filteredOutputs.filter(o => !o.outputType?.isLocked);
        }

        setOutputs(filteredOutputs);
        setTotal(response.data.total || filteredOutputs.length);
      }
    } catch (error) {
      console.error('Error fetching outputs:', error);
      enqueueSnackbar(t ? t('qbus.fetchError') : 'Failed to load Qbus outputs', { variant: 'error' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [locationId, page, rowsPerPage, search, selectedType, selectedTab, showOnlyAssigned, isAdmin, enqueueSnackbar, t]);

  // Initial load
  useEffect(() => {
    fetchOutputTypes();
    fetchControllers();
    fetchFolders();
  }, [fetchOutputTypes, fetchControllers, fetchFolders]);

  useEffect(() => {
    if (locationId) {
      fetchOutputs();
    }
  }, [fetchOutputs, locationId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(0);
  };

  // Handle send command
  const handleSendCommand = async (outputId, value) => {
    if (!canControl) {
      enqueueSnackbar(t ? t('qbus.noControlPermission') : 'You do not have permission to control outputs', { variant: 'warning' });
      return;
    }

    try {
      await qbusApi.sendOutputCommand(outputId, value);
      enqueueSnackbar(t ? t('qbus.commandSent') : 'Command sent successfully', { variant: 'success' });

      // Optimistically update the output state
      setOutputs(prev => prev.map(o =>
        o.id === outputId
          ? { ...o, current_value: { ...o.current_value, value } }
          : o
      ));
    } catch (error) {
      console.error('Error sending command:', error);
      enqueueSnackbar(t ? t('qbus.commandError') : 'Failed to send command', { variant: 'error' });
    }
  };

  // Handle update output (name, folder, forward_to_api)
  const handleUpdateOutput = async (outputId, updateData) => {
    if (!canEdit) {
      enqueueSnackbar(t ? t('qbus.noEditPermission') : 'You do not have permission to edit outputs', { variant: 'warning' });
      return;
    }

    try {
      await qbusApi.updateOutput(outputId, updateData);
      enqueueSnackbar(t ? t('qbus.outputUpdated') : 'Output updated successfully', { variant: 'success' });

      // Update local state
      setOutputs(prev => prev.map(o =>
        o.id === outputId ? { ...o, ...updateData } : o
      ));
    } catch (error) {
      console.error('Error updating output:', error);
      enqueueSnackbar(t ? t('qbus.updateError') : 'Failed to update output', { variant: 'error' });
    }
  };

  // Handle update name (wrapper for backward compat)
  const handleUpdateName = async (outputId, friendlyName) => {
    await handleUpdateOutput(outputId, { friendlyName });
  };

  // Handle view history
  const handleViewHistory = async (output) => {
    setHistoryOutput(output);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);

    try {
      const response = await qbusApi.getOutputHistory(output.id, { limit: 50 });
      if (response.data.history) {
        setHistoryData(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      enqueueSnackbar(t ? t('qbus.historyError') : 'Failed to load history', { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  };

  // Folder management
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await qbusApi.createFolder(locationId, { name: newFolderName.trim() });
      setNewFolderName('');
      fetchFolders();
      enqueueSnackbar('Folder created', { variant: 'success' });
    } catch (error) {
      console.error('Error creating folder:', error);
      enqueueSnackbar('Failed to create folder', { variant: 'error' });
    }
  };

  const handleUpdateFolder = async (folderId, data) => {
    try {
      await qbusApi.updateFolder(folderId, data);
      fetchFolders();
      setEditingFolder(null);
    } catch (error) {
      console.error('Error updating folder:', error);
      enqueueSnackbar('Failed to update folder', { variant: 'error' });
    }
  };

  const handleDeleteFolder = (folder) => {
    setDeletingFolder(folder);
  };

  const handleConfirmDeleteFolder = async () => {
    if (!deletingFolder) return;
    const folderId = deletingFolder.id;
    try {
      await qbusApi.deleteFolder(folderId);
      fetchFolders();
      fetchOutputs(true);
      if (selectedTab === String(folderId)) {
        setSelectedTab(TAB_ALL);
      }
      enqueueSnackbar('Folder deleted', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting folder:', error);
      enqueueSnackbar('Failed to delete folder', { variant: 'error' });
    } finally {
      setDeletingFolder(null);
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearch('');
    setSelectedType('');
    setShowOnlyAssigned(false);
    setPage(0);
  };

  // Format change source
  const formatChangeSource = (source) => {
    const sourceMap = {
      user: { label: t ? t('qbus.history.user') : 'User', color: 'primary' },
      automation: { label: t ? t('qbus.history.automation') : 'Automation', color: 'secondary' },
      physical: { label: t ? t('qbus.history.physical') : 'Physical', color: 'default' },
      system: { label: t ? t('qbus.history.system') : 'System', color: 'info' }
    };
    return sourceMap[source] || { label: source, color: 'default' };
  };

  // Connection status color
  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'reconnecting': return 'warning';
      case 'error':
      case 'unavailable': return 'error';
      default: return 'default';
    }
  };

  // Visible folders (respecting showHidden toggle)
  const visibleFolders = folders.filter(f => showHidden || !f.is_hidden);

  // No controllers check
  if (!loading && controllers.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t ? t('qbus.noControllers') : 'No Qbus controllers found at this location.'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">
            {t ? t('qbus.controlPanel.title') : 'Domotics Control'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t ? t('qbus.controlPanel.subtitle', { count: total }) : `${total} outputs at ${locationName || 'this location'}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Live connection status */}
          <Chip
            icon={<DotIcon sx={{ fontSize: 12 }} />}
            label={isConnected ? 'Live' : connectionStatus === 'reconnecting' ? 'Reconnecting' : 'Offline'}
            size="small"
            color={getConnectionColor()}
            variant={isConnected ? 'filled' : 'outlined'}
          />
          <Tooltip title={t ? t('common.refresh') : 'Refresh'}>
            <IconButton onClick={() => fetchOutputs(true)} disabled={refreshing}>
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <>
              <Tooltip title="Bulk Assign Friendly Names">
                <IconButton onClick={() => setBulkNameDialogOpen(true)}>
                  <BulkNameIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Manage Folders">
                <IconButton onClick={() => setFolderDialogOpen(true)}>
                  <CreateFolderIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t ? t('qbus.controlPanel.manageTypes') : 'Manage Output Types'}>
                <IconButton onClick={() => window.open('/qbus-output-types', '_blank')}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Folder Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1 }}
          >
            <Tab label={t ? t('common.all') : 'All'} value={TAB_ALL} />
            {visibleFolders.map(folder => (
              <Tab
                key={folder.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {folder.is_hidden && <VisibilityOffIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                    {folder.name}
                    {folder.outputCount !== undefined && (
                      <Chip label={folder.outputCount} size="small" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />
                    )}
                  </Box>
                }
                value={String(folder.id)}
              />
            ))}
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FolderOffIcon sx={{ fontSize: 16 }} />
                  {t ? t('qbus.folders.unorganized') : 'Unorganized'}
                </Box>
              }
              value={TAB_UNORGANIZED}
            />
          </Tabs>
          {isAdmin && folders.some(f => f.is_hidden) && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                />
              }
              label={<Typography variant="caption">Show hidden</Typography>}
              sx={{ mr: 2, ml: 1 }}
            />
          )}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t ? t('common.search') : 'Search outputs...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t ? t('qbus.controlPanel.outputType') : 'Output Type'}</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                label={t ? t('qbus.controlPanel.outputType') : 'Output Type'}
              >
                <MenuItem value="">{t ? t('common.all') : 'All'}</MenuItem>
                {outputTypes
                  .filter(type => isAdmin || !type.isLocked)
                  .map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.friendlyName}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              label={showOnlyAssigned
                ? (t ? t('qbus.controlPanel.assignedOnly') : 'Assigned Only')
                : (t ? t('qbus.controlPanel.showAll') : 'Show All')
              }
              onClick={() => setShowOnlyAssigned(!showOnlyAssigned)}
              color={showOnlyAssigned ? 'primary' : 'default'}
              variant={showOnlyAssigned ? 'filled' : 'outlined'}
              icon={<FilterIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            {(search || selectedType || showOnlyAssigned) && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
              >
                {t ? t('common.clearFilters') : 'Clear'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Controllers summary */}
      {controllers.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {controllers.map(controller => (
            <Chip
              key={controller.id}
              label={controller.friendlyName || `Controller ${controller.id}`}
              size="small"
              color={controller.status === 'online' ? 'success' : 'error'}
              variant="outlined"
            />
          ))}
        </Box>
      )}

      {/* Output cards */}
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : outputs.length === 0 ? (
        <Alert severity="info">
          {t ? t('qbus.noOutputs') : 'No outputs found matching your filters.'}
        </Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {outputs.map(output => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={output.id}>
                <QbusOutputCard
                  output={output}
                  folders={folders}
                  onSendCommand={handleSendCommand}
                  onUpdateName={handleUpdateName}
                  onUpdateOutput={handleUpdateOutput}
                  onViewHistory={handleViewHistory}
                  isAdmin={isAdmin}
                  canEdit={canEdit}
                  disabled={!canControl}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Box>
        </>
      )}

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            <Typography variant="h6">
              {t ? t('qbus.history.title') : 'State Change History'}
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {historyOutput && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {historyOutput.friendlyName || historyOutput.name || historyOutput.output_id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {historyOutput.outputType?.friendlyName || 'Unknown Type'}
              </Typography>
            </Box>
          )}

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyData.length === 0 ? (
            <Alert severity="info">
              {t ? t('qbus.history.empty') : 'No history records found.'}
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t ? t('qbus.history.time') : 'Time'}</TableCell>
                    <TableCell>{t ? t('qbus.history.previousValue') : 'Previous'}</TableCell>
                    <TableCell>{t ? t('qbus.history.newValue') : 'New'}</TableCell>
                    <TableCell>{t ? t('qbus.history.source') : 'Source'}</TableCell>
                    <TableCell>{t ? t('qbus.history.user') : 'User'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((record) => {
                    const sourceInfo = formatChangeSource(record.change_source);
                    return (
                      <TableRow key={record.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(record.changed_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {JSON.stringify(record.previous_value?.value ?? record.previous_value)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {JSON.stringify(record.new_value?.value ?? record.new_value)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sourceInfo.label}
                            color={sourceInfo.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {record.changedByUser
                            ? `${record.changedByUser.firstName} ${record.changedByUser.lastName}`
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>
            {t ? t('common.close') : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Management Dialog */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => { setFolderDialogOpen(false); setEditingFolder(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderIcon />
            <Typography variant="h6">Manage Folders</Typography>
          </Box>
          <IconButton onClick={() => { setFolderDialogOpen(false); setEditingFolder(null); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Create new folder */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="New folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
            >
              Add
            </Button>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {/* Folder list */}
          {folders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No folders yet. Create one above.
            </Typography>
          ) : (
            <List dense>
              {folders.map((folder) => (
                <ListItem key={folder.id}>
                  {editingFolder === folder.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <TextField
                        size="small"
                        fullWidth
                        defaultValue={folder.name}
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateFolder(folder.id, { name: e.target.value });
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value !== folder.name) {
                            handleUpdateFolder(folder.id, { name: e.target.value });
                          } else {
                            setEditingFolder(null);
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={folder.name}
                        secondary={`${folder.outputCount ?? 0} outputs`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={folder.is_hidden ? 'Show folder' : 'Hide folder'}>
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateFolder(folder.id, { is_hidden: !folder.is_hidden })}
                          >
                            {folder.is_hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rename">
                          <IconButton size="small" onClick={() => setEditingFolder(folder.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete folder">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteFolder(folder)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFolderDialogOpen(false); setEditingFolder(null); }}>
            {t ? t('common.close') : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Friendly Name Dialog */}
      <BulkFriendlyNameDialog
        open={bulkNameDialogOpen}
        onClose={() => setBulkNameDialogOpen(false)}
        locationId={locationId}
        onApplied={() => {
          fetchOutputs(true);
          enqueueSnackbar('Friendly names updated', { variant: 'success' });
        }}
      />

      {/* Delete Folder Confirmation Dialog */}
      <Dialog
        open={!!deletingFolder}
        onClose={() => setDeletingFolder(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete folder?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the folder <strong>"{deletingFolder?.name}"</strong>?
          </Typography>
          {(deletingFolder?.outputCount > 0) && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              This folder has <strong>{deletingFolder.outputCount}</strong> linked output(s).
              They will be moved to <strong>Unorganized</strong>.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFolder(null)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDeleteFolder}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QbusControlPanel;
